/* =============================================================================
   B2B Med Spa Contact Form — assets/b2b-med-spa-contact-form.js

   Drives sections/b2b-med-spa-contact-form.liquid only. Scoped to
   [data-b2bcf-form], registers no globals beyond a single init guard, and
   never touches the existing TS Contact section or the HulkApps widget.

   Responsibilities
     1. Auto-fill hidden tracking fields (UTM, gclid, referrer, device, ...)
        using first-touch values remembered for the visit.
     2. Client-side validation with per-field messages.
     3. Submit: build the payload, POST it to the configured API endpoint and,
        always and in parallel, through Shopify's own /contact form so the
        store notification email goes out whether the API succeeds or not.
   ========================================================================== */

(function () {
  "use strict";

  /* One <script> tag is emitted per section instance, so this file can run
     several times on one page. Forms carry their own __b2bcfReady guard, and
     the flag below keeps the document-level listeners registered only once. */

  var TOUCH_KEY = "b2bcf_first_touch";

  var FREE_EMAIL_DOMAINS = [
    "gmail.com",
    "googlemail.com",
    "yahoo.com",
    "yahoo.co.uk",
    "yahoo.co.in",
    "ymail.com",
    "rocketmail.com",
    "hotmail.com",
    "hotmail.co.uk",
    "outlook.com",
    "live.com",
    "msn.com",
    "aol.com",
    "icloud.com",
    "me.com",
    "mac.com",
    "gmx.com",
    "gmx.net",
    "mail.com",
    "zoho.com",
    "yandex.com",
    "yandex.ru",
    "protonmail.com",
    "proton.me",
    "pm.me",
    "tutanota.com",
    "hushmail.com",
    "comcast.net",
    "verizon.net",
    "att.net",
    "sbcglobal.net",
    "bellsouth.net",
    "cox.net",
    "charter.net",
    "earthlink.net",
    "juno.com",
    "optonline.net",
    "mailinator.com",
    "guerrillamail.com",
    "yopmail.com",
    "sharklasers.com",
    "10minutemail.com",
    "temp-mail.org",
    "tempmail.com",
    "trashmail.com",
    "throwawaymail.com",
    "getnada.com",
    "dispostable.com",
    "maildrop.cc",
    "fakeinbox.com",
    "mailnesia.com",
    "moakt.com",
    "emailondeck.com",
  ];

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;
  var URL_RE = /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(\/\S*)?$/i;
  var PHONE_RE = /^[+()\d][\d\s().-]{6,}$/;

  /* ---------------------------------------------------------------- helpers */

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function queryParam(name) {
    try {
      return new URLSearchParams(window.location.search).get(name) || "";
    } catch (e) {
      return "";
    }
  }

  function safeParseJson(text, fallback) {
    if (!text) return fallback;
    try {
      return JSON.parse(text);
    } catch (e) {
      return fallback;
    }
  }

  /* Promise.allSettled in a form Safari 12 and older also understand. */
  function settle(promise) {
    return promise.then(
      function (value) {
        return { ok: true, value: value };
      },
      function (error) {
        return { ok: false, error: error };
      },
    );
  }

  function detectBrowser() {
    var ua = navigator.userAgent;
    if (/Edg\//.test(ua)) return "Edge";
    if (/OPR\/|Opera/.test(ua)) return "Opera";
    if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return "Chrome";
    if (/Firefox\//.test(ua)) return "Firefox";
    if (/Safari\//.test(ua)) return "Safari";
    if (/MSIE|Trident/.test(ua)) return "Internet Explorer";
    return "Other";
  }

  function detectDevice() {
    var ua = navigator.userAgent;
    var os = "Unknown OS";

    if (/iPhone|iPod/.test(ua)) os = "iOS";
    else if (/iPad/.test(ua)) os = "iPadOS";
    else if (/Android/.test(ua)) os = "Android";
    else if (/Mac OS X|Macintosh/.test(ua)) os = "macOS";
    else if (/Windows/.test(ua)) os = "Windows";
    else if (/Linux/.test(ua)) os = "Linux";

    var type = "Desktop";
    if (/Mobi|iPhone|iPod|Android.*Mobile/.test(ua)) type = "Mobile";
    else if (/iPad|Tablet|Android/.test(ua)) type = "Tablet";

    return type + " / " + os;
  }

  /* First-touch data: captured once per browsing session so a lead that lands
     on a UTM-tagged page and converts three pages later still carries its
     attribution. */
  function firstTouch() {
    var stored = null;

    try {
      stored = safeParseJson(window.sessionStorage.getItem(TOUCH_KEY), null);
    } catch (e) {
      stored = null;
    }

    if (stored && typeof stored === "object") return stored;

    var data = {
      utm_source: queryParam("utm_source"),
      utm_medium: queryParam("utm_medium"),
      utm_campaign: queryParam("utm_campaign"),
      utm_term: queryParam("utm_term"),
      utm_content: queryParam("utm_content"),
      gclid: queryParam("gclid"),
      fbclid: queryParam("fbclid"),
      ref: queryParam("ref"),
      landing_page: window.location.href,
      landing_path: window.location.pathname + window.location.search,
      referrer: document.referrer || "",
    };

    try {
      window.sessionStorage.setItem(TOUCH_KEY, JSON.stringify(data));
    } catch (e) {
      /* private mode — fall through with in-memory values */
    }

    return data;
  }

  function trackingValue(source) {
    var touch = firstTouch();

    switch (source) {
      case "utm_source":
      case "utm_medium":
      case "utm_campaign":
      case "utm_term":
      case "utm_content":
      case "gclid":
      case "fbclid":
      case "ref":
        return queryParam(source) || touch[source] || "";
      case "landing_page":
        return touch.landing_page || window.location.href;
      case "landing_path":
        /* Sessions stored before this option existed only kept the full URL. */
        if (touch.landing_path) return touch.landing_path;
        if (touch.landing_page) {
          try {
            var landing = new URL(touch.landing_page);
            return landing.pathname + landing.search;
          } catch (e) {
            /* fall through to the current page */
          }
        }
        return window.location.pathname + window.location.search;
      case "referrer":
        return touch.referrer || document.referrer || "";
      case "page_url":
        return window.location.href;
      case "page_title":
        return document.title || "";
      case "device":
        return detectDevice();
      case "browser":
        return detectBrowser();
      case "timestamp":
        return new Date().toISOString();
      default:
        return "";
    }
  }

  /* Resolves every hidden tracking field against the current URL and session.
     Called once at init and again immediately before submit — re-reading costs
     nothing and means a value wiped by a bfcache restore, a password manager,
     or a third-party script can never be sent empty. Returns a label -> value
     map for the debug log. */
  function refreshTracking(form) {
    var resolved = {};

    toArray(form.querySelectorAll("[data-b2bcf-hidden]")).forEach(function (input) {
      var source = input.getAttribute("data-b2bcf-source");
      var fallback = input.getAttribute("data-b2bcf-fallback") || "";
      var label = input.getAttribute("data-b2bcf-label") || input.getAttribute("name");

      if (source && source !== "none") {
        var value = trackingValue(source);
        if (value) input.value = value;
      }

      if (!input.value) input.value = fallback;

      resolved[label] = input.value;
    });

    return resolved;
  }

  function parseHeaders(text) {
    var headers = {};
    if (!text) return headers;

    text.split(/\r?\n/).forEach(function (line) {
      var idx = line.indexOf(":");
      if (idx < 1) return;
      var key = line.slice(0, idx).trim();
      var value = line.slice(idx + 1).trim();
      if (key) headers[key] = value;
    });

    return headers;
  }

  function parseFieldMap(text) {
    var map = {};
    if (!text) return map;

    text.split(/\r?\n/).forEach(function (line) {
      var parts = line.split("=>");
      if (parts.length !== 2) return;
      var from = parts[0].trim();
      var to = parts[1].trim();
      if (from && to) map[from] = to;
    });

    return map;
  }

  /* ------------------------------------------------------------- validation */

  function fieldWrapper(input) {
    return input.closest("[data-b2bcf-field]");
  }

  function showError(input, message) {
    var wrap = fieldWrapper(input);
    if (!wrap) return;
    var slot = wrap.querySelector("[data-b2bcf-error]");
    wrap.classList.add("is-invalid");
    if (slot) slot.textContent = message;
    input.setAttribute("aria-invalid", "true");
  }

  function clearError(input) {
    var wrap = fieldWrapper(input);
    if (!wrap) return;
    var slot = wrap.querySelector("[data-b2bcf-error]");
    wrap.classList.remove("is-invalid");
    if (slot) slot.textContent = "";
    input.removeAttribute("aria-invalid");
  }

  /* An email field is not always type="email". The block's Input type setting
     can be left on Text — by accident, or on purpose to dodge the browser's own
     bubble — and the validation below used to key off the DOM type alone, so
     the whole email check silently did nothing on a field plainly labelled
     Email. The label and name are consulted as well, since those are what the
     merchant actually names the field. */
  function isEmailField(input) {
    if (input.type === "email") return true;
    if (input.hasAttribute("data-b2bcf-business-email")) return true;

    var hint = (input.getAttribute("data-b2bcf-label") || input.getAttribute("name") || "").toLowerCase();

    return /\be-?mail\b/.test(hint);
  }

  function isFreeEmail(value) {
    var domain = String(value).split("@")[1];
    if (!domain) return false;
    domain = domain.toLowerCase().trim();
    return FREE_EMAIL_DOMAINS.indexOf(domain) !== -1;
  }

  function validateField(input, config) {
    var value = input.type === "checkbox" ? input.checked : String(input.value || "").trim();
    var required = input.hasAttribute("data-b2bcf-required");

    if (input.type === "checkbox") {
      if (required && !value) {
        showError(input, config.requiredMessage);
        return false;
      }
      clearError(input);
      return true;
    }

    if (required && !value) {
      showError(input, config.requiredMessage);
      return false;
    }

    if (!value) {
      clearError(input);
      return true;
    }

    var minLength = parseInt(input.getAttribute("data-b2bcf-min-length") || "0", 10);
    if (minLength > 0 && value.length < minLength) {
      showError(input, "Please enter at least " + minLength + " characters.");
      return false;
    }

    if (isEmailField(input)) {
      if (!EMAIL_RE.test(value)) {
        showError(input, config.emailMessage);
        return false;
      }
      if (input.hasAttribute("data-b2bcf-business-email") && isFreeEmail(value)) {
        showError(input, config.businessEmailMessage);
        return false;
      }
    }

    if (input.type === "url" && !URL_RE.test(value)) {
      showError(input, config.urlMessage);
      return false;
    }

    if (input.type === "tel" && !PHONE_RE.test(value)) {
      showError(input, config.phoneMessage);
      return false;
    }

    var pattern = input.getAttribute("data-b2bcf-pattern");
    if (pattern) {
      try {
        if (!new RegExp(pattern).test(value)) {
          showError(input, config.requiredMessage);
          return false;
        }
      } catch (e) {
        /* an invalid regex in the theme editor must not block a submission */
      }
    }

    clearError(input);
    return true;
  }

  /* ----------------------------------------------------------------- submit */

  function collectPayload(form) {
    var payload = {};

    toArray(form.querySelectorAll("[name]")).forEach(function (input) {
      var name = input.getAttribute("name");
      if (!name || name === "b2bcf_hp") return;

      if (input.type === "checkbox") {
        payload[name] = input.checked ? input.value : input.getAttribute("data-b2bcf-unchecked-value") || "";
        return;
      }

      payload[name] = String(input.value || "").trim();
    });

    return payload;
  }

  function buildBody(payload, config) {
    if (config.format === "form") {
      var params = new URLSearchParams();
      Object.keys(payload).forEach(function (key) {
        params.append(key, payload[key]);
      });
      return { body: params, contentType: "application/x-www-form-urlencoded;charset=UTF-8" };
    }

    if (config.format === "multipart") {
      var data = new FormData();
      Object.keys(payload).forEach(function (key) {
        data.append(key, payload[key]);
      });
      /* the browser sets the multipart boundary itself — never set it manually */
      return { body: data, contentType: null };
    }

    var json = payload;
    if (config.wrapKey) {
      json = {};
      json[config.wrapKey] = payload;
    }

    return { body: JSON.stringify(json), contentType: "application/json" };
  }

  /* A 4xx carrying a message is the endpoint refusing the submission — a
     duplicate clinic, most often — rather than a transport failure. It has to
     fail the form even when the email notification went out, because the lead
     reaching an inbox does not mean the clinic was created, and telling the
     visitor it worked leaves them waiting for something that never happens.
     5xx is left out: that is the API being broken, and the email copy really is
     a good enough fallback for it. */
  function apiRefusal(api) {
    if (api.ok || !api.error) return "";
    if (!api.error.status || api.error.status < 400 || api.error.status >= 500) return "";

    return api.error.apiMessage || "";
  }

  /* Puts the endpoint's complaint under the field it is about, so "a clinic
     with this email already exists" lands on the email box instead of only at
     the foot of the form. The wording is all the endpoint gives us to go on, so
     this matches on it and does nothing when it recognises neither. */
  function conflictField(form, message) {
    var text = String(message || "").toLowerCase();
    var inputs = toArray(form.querySelectorAll("[data-b2bcf-label]"));
    var i;

    if (text.indexOf("email") !== -1) {
      for (i = 0; i < inputs.length; i++) {
        if (isEmailField(inputs[i])) return inputs[i];
      }
      return null;
    }

    if (text.indexOf("name") !== -1) {
      /* "Practice / Clinic Name" rather than First or Last — a clinic-name
         conflict is never about the contact's own name. */
      for (i = 0; i < inputs.length; i++) {
        var label = (inputs[i].getAttribute("data-b2bcf-label") || "").toLowerCase();
        if (label.indexOf("clinic") !== -1 || label.indexOf("practice") !== -1) return inputs[i];
      }
    }

    return null;
  }

  function sendToApi(payload, config) {
    if (!config.endpoint) return Promise.resolve({ skipped: true });

    var built = buildBody(payload, config);
    var headers = parseHeaders(config.headers);

    if (built.contentType && !headers["Content-Type"] && !headers["content-type"]) {
      headers["Content-Type"] = built.contentType;
    }

    var options = {
      method: config.method || "POST",
      body: built.body,
      headers: headers,
    };

    if (config.credentials) options.credentials = "include";

    if (config.noCors) {
      options.mode = "no-cors";
      /* no-cors only allows simple headers; anything else throws */
      options.headers = {};
      if (config.format === "json") {
        options.body = built.body;
        options.headers = { "Content-Type": "text/plain;charset=UTF-8" };
      }
    }

    return fetch(config.endpoint, options).then(function (response) {
      if (config.noCors) return { ok: true, opaque: true };
      if (!response.ok) {
        return response.text().then(function (text) {
          /* The status and the endpoint's own message are carried on the error
             so the caller can tell a refusal ("a clinic with this email already
             exists") from a server or network failure, and can show what the
             endpoint actually said instead of a generic apology. */
          var body = safeParseJson(text, null);
          var error = new Error("HTTP " + response.status + " — " + text.slice(0, 300));

          error.status = response.status;
          error.body = body;
          error.apiMessage = body && typeof body === "object" ? String(body.message || "") : "";

          throw error;
        });
      }
      return response.text().then(function (text) {
        return { ok: true, body: safeParseJson(text, text) };
      });
    });
  }

  /* Payload key -> the label the visitor actually saw. Payload keys are written
     for the receiving API (lowercase, underscores), which reads badly in an
     inbox, so the notification email is rebuilt from the labels instead. */
  function labelLookup(form) {
    var labels = {};

    toArray(form.querySelectorAll("[name][data-b2bcf-label]")).forEach(function (input) {
      var label = input.getAttribute("data-b2bcf-label");
      if (label) labels[input.getAttribute("name")] = label;
    });

    return labels;
  }

  /* Every submitted value, as a readable block of text. Shopify's default
     "Contact customer" notification only prints the fields it knows about, so
     the summary is dropped into contact[body] to guarantee the store owner
     receives everything the visitor typed. */
  function buildSummary(payload, labels) {
    return Object.keys(payload)
      .filter(function (key) {
        return String(payload[key] || "").trim() !== "";
      })
      .map(function (key) {
        return (labels[key] || key) + ": " + payload[key];
      })
      .join("\n");
  }

  /* Shopify rejects a contact submission without an email address, so fall
     back to the first value in the payload that looks like one. */
  function findEmail(form, payload) {
    var input = form.querySelector('input[type="email"][name]');
    if (input && String(input.value || "").trim()) return input.value.trim();

    var keys = Object.keys(payload);

    for (var i = 0; i < keys.length; i++) {
      var value = String(payload[keys[i]] || "").trim();
      if (EMAIL_RE.test(value)) return value;
    }

    return "";
  }

  /* The email notification runs on every submit, in parallel with the API call,
     and neither one can prevent the other. The only way to stop it is the
     "Send the email notification" setting, meant for testing an endpoint
     without filling the store inbox. */
  function sendEmailNotification(payload, config, form) {
    if (config.sendEmail === false) return Promise.resolve({ skipped: true });

    var map = parseFieldMap(config.emailFieldMap);
    var labels = labelLookup(form);
    var params = new URLSearchParams();
    var targets = {};

    params.append("form_type", "contact");
    params.append("utf8", "✓");

    /* An explicit mapping wins; otherwise the field's own label is used, so the
       notification reads "Practice / Clinic Name" rather than "practice_name". */
    Object.keys(payload).forEach(function (key) {
      var target = map[key] || "contact[" + (labels[key] || key) + "]";
      params.append(target, payload[key]);
      targets[target] = true;
    });

    if (!targets["contact[email]"]) {
      params.append("contact[email]", findEmail(form, payload));
    }

    if (config.emailBodySummary !== false && !targets["contact[body]"]) {
      params.append("contact[body]", buildSummary(payload, labels));
    }

    return fetch("/contact", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
      body: params,
    }).then(function (response) {
      if (!response.ok) {
        throw new Error("Shopify /contact responded " + response.status);
      }
      return { ok: true };
    });
  }

  /* ------------------------------------------------------------------- init */

  function initForm(form) {
    if (form.__b2bcfReady) return;
    form.__b2bcfReady = true;

    var root = form.closest(".b2bcf") || form.parentNode;
    var configEl = root.querySelector("[data-b2bcf-config]");
    var config = safeParseJson(configEl && configEl.textContent, {}) || {};

    var button = form.querySelector("[data-b2bcf-submit]");
    var successEl = form.querySelector("[data-b2bcf-success]");
    var failureEl = form.querySelector("[data-b2bcf-failure]");
    var honeypot = form.querySelector("[data-b2bcf-honeypot]");

    /* 1. hidden tracking fields */
    refreshTracking(form);

    /* 2. clear a field's error as soon as the visitor fixes it

       Email and URL fields are listed explicitly. The selector used to cover
       only required, pattern and min-length fields, so an optional email got no
       check until submit — the visitor filled in a typo, tabbed away with no
       complaint, finished the rest of the form, and only then found out. */
    toArray(
      form.querySelectorAll(
        "[data-b2bcf-required], [data-b2bcf-pattern], [data-b2bcf-min-length]," +
          ' [data-b2bcf-business-email], input[type="email"], input[type="url"]',
      ),
    ).forEach(
      function (input) {
        var event = input.tagName === "SELECT" || input.type === "checkbox" ? "change" : "blur";

        input.addEventListener(event, function () {
          validateField(input, config);
        });

        input.addEventListener("input", function () {
          if (fieldWrapper(input) && fieldWrapper(input).classList.contains("is-invalid")) {
            validateField(input, config);
          }
        });
      },
    );

    /* 3. submit */
    form.addEventListener("submit", function (event) {
      event.preventDefault();

      if (form.classList.contains("is-loading")) return;

      if (successEl) successEl.hidden = true;
      if (failureEl) failureEl.hidden = true;

      /* bot check — pretend everything went fine, send nothing */
      if (honeypot && honeypot.value) {
        if (successEl) {
          successEl.textContent = config.successMessage;
          successEl.hidden = false;
        }
        return;
      }

      var inputs = toArray(form.querySelectorAll("input, select, textarea")).filter(function (el) {
        return el.type !== "hidden" && el !== honeypot;
      });

      var firstInvalid = null;

      inputs.forEach(function (input) {
        if (!validateField(input, config) && !firstInvalid) firstInvalid = input;
      });

      if (firstInvalid) {
        firstInvalid.focus();
        if (typeof firstInvalid.scrollIntoView === "function") {
          firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return;
      }

      /* Re-resolve tracking immediately before reading the values, so the
         payload can never carry a hidden field that was emptied after init. */
      var tracking = refreshTracking(form);

      var payload = collectPayload(form);

      /* merge the fixed fields configured in the theme editor */
      var extra = safeParseJson(config.extraPayload, null);
      if (extra && typeof extra === "object") {
        Object.keys(extra).forEach(function (key) {
          payload[key] = extra[key];
        });
      }

      if (config.debug) {
        /* eslint-disable no-console */
        console.groupCollapsed("[b2b-med-spa-contact-form] submitting");
        console.log("payload", payload);

        if (typeof console.table === "function") {
          console.table(tracking);
        } else {
          console.log("tracking", tracking);
        }

        console.log("config", config);
        console.groupEnd();
        /* eslint-enable no-console */
      }

      form.classList.add("is-loading");
      if (button) button.disabled = true;

      /* Both requests are started in the same tick and neither is chained to
         the other, so a rejected API call can never stop the email going out —
         and a rejected email can never stop the API call. */
      Promise.all([settle(sendToApi(payload, config)), settle(sendEmailNotification(payload, config, form))])
        .then(function (results) {
          var api = results[0];
          var email = results[1];

          /* "skipped" means the channel was switched off in the theme editor —
             no endpoint set, or the email notification turned off for testing.
             That is not a delivery, so it must not count as one. */
          var apiDelivered = api.ok && !(api.value && api.value.skipped === true);
          var emailDelivered = email.ok && !(email.value && email.value.skipped === true);

          if (config.debug) {
            /* eslint-disable-next-line no-console */
            console.log("[b2b-med-spa-contact-form] api", api, "email", email);
          }

          if (!api.ok) {
            /* eslint-disable-next-line no-console */
            console.error("[b2b-med-spa-contact-form] API call failed", api.error);
          }

          if (!email.ok) {
            /* eslint-disable-next-line no-console */
            console.error("[b2b-med-spa-contact-form] email notification failed", email.error);
          }

          if (config.sendEmail === false) {
            /* eslint-disable-next-line no-console */
            console.warn("[b2b-med-spa-contact-form] email notification is OFF — this lead was sent to the API only");
          }

          /* The lead is safe as long as one of the two channels went through —
             unless the API refused it outright, which no amount of email can
             make true. A duplicate clinic has to be reported as a failure even
             though the notification was delivered. */
          var refusal = apiRefusal(api);

          if (refusal || (!emailDelivered && !apiDelivered)) {
            var message = refusal || config.errorMessage;

            if (failureEl) {
              failureEl.textContent = message;
              failureEl.hidden = false;
            }

            if (refusal) {
              var field = conflictField(form, refusal);

              if (field) {
                showError(field, refusal);
                field.focus();

                if (typeof field.scrollIntoView === "function") {
                  field.scrollIntoView({ behavior: "smooth", block: "center" });
                }
              }
            }

            form.dispatchEvent(
              new CustomEvent("b2b-med-spa-contact-form:error", {
                bubbles: true,
                detail: {
                  payload: payload,
                  api: api,
                  email: email,
                  refused: Boolean(refusal),
                  message: message,
                },
              }),
            );

            return;
          }

          if (successEl) {
            successEl.textContent = config.successMessage;
            successEl.hidden = false;
          }

          form.dispatchEvent(
            new CustomEvent("b2b-med-spa-contact-form:success", {
              bubbles: true,
              detail: {
                payload: payload,
                api: api,
                email: email,
              },
            }),
          );

          if (config.gtmEvent) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: config.gtmEvent, formData: payload });
          }

          if (config.resetOnSuccess) {
            form.reset();
            toArray(form.querySelectorAll("[data-b2bcf-field]")).forEach(function (wrap) {
              wrap.classList.remove("is-invalid");
            });
          }

          if (config.hideOnSuccess) form.classList.add("is-sent");

          if (config.redirectUrl) {
            window.setTimeout(function () {
              window.location.href = config.redirectUrl;
            }, 800);
          }
        })
        .catch(function (error) {
          /* eslint-disable-next-line no-console */
          console.error("[b2b-med-spa-contact-form] unexpected error", error);
        })
        .then(function () {
          form.classList.remove("is-loading");
          if (button) button.disabled = false;
        });
    });
  }

  function initAll(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    toArray(context.querySelectorAll("[data-b2bcf-form]")).forEach(initForm);
  }

  initAll(document);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll(document);
    });
  }

  if (!window.__b2bcfBound) {
    window.__b2bcfBound = true;

    /* theme editor: re-init when the section is re-rendered */
    document.addEventListener("shopify:section:load", function (event) {
      initAll(event.target);
    });
  }
})();